import { Component, OnInit } from '@angular/core';
import { UserService } from '../shared/user.service';
import { Archive, ArchiveRestore, Edit, LucideAngularModule, Plus } from 'lucide-angular';
import { PagedEntities } from '@app/shared/abstract/paged-entities';
import { UserModel } from '../shared/user.model';
import { roleLabels } from '../shared/user-roles';
import { RouteParams } from '@app/shared/abstract/route-params';
import { Column } from '@app/partials/datatable/data-table/column';
import { DataTableComponent } from '@app/partials/datatable/data-table/data-table.component';
import { RouterLink } from '@angular/router';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { NotificationService } from '@app/shared/notification/notification.service';
import { AuthService } from '@app/auth/auth.service';
import { SelectComponent } from '@app/partials/form/select/select.component';
import { ConfirmationModalService } from '@app/partials/modal/confirmation-modal.service';
import { EventRoleNamesManagerComponent } from './event-role-names-manager.component';

@Component({
  selector: 'app-index11',
  imports: [DataTableComponent, LucideAngularModule, RouterLink, ReactiveFormsModule, SelectComponent, EventRoleNamesManagerComponent],
  templateUrl: './users-index.component.html',
  styleUrl: './users-index.component.css',
})
export class UsersIndexComponent implements OnInit {
  protected currentUser: UserModel | null = null;
  protected isAdmin = false;

  constructor(
    private service: UserService,
    private notificationService: NotificationService,
    private authService: AuthService,
    private confirmationModal: ConfirmationModalService,
  ) {}

  /**
   * TVG-83 / TVG-312: whether to show the admin block for managing the Sales
   * Manager, Show around and lead source lists. Admin-only. The block itself
   * drops the two ownership lists when the event-ownership-fields toggle is
   * off, so it stays useful for lead source alone.
   */
  protected get canManageRoleNames(): boolean {
    return this.isAdmin;
  }

  protected readonly PlusIcon = Plus;
  protected readonly EditIcon = Edit;
  protected readonly ArchiveIcon = Archive;
  protected readonly RestoreIcon = ArchiveRestore;

  protected usersPage?: PagedEntities<UserModel>;
  protected archivedFilterControl = new FormControl<'all' | 'active' | 'archived'>('active', { nonNullable: true });

  protected archivedFilterChoices = [
    { value: 'active', label: 'Active Only' },
    { value: 'all', label: 'All Users' },
    { value: 'archived', label: 'Archived Only' },
  ];

  routeParams = new RouteParams({
    pageNum: 1,
    pageSize: 100,
  });

  tableColumns: Column<UserModel>[] = [
    { field: 'name', header: 'Name', sortable: true },
    { field: 'email', header: 'Email' },
    // TVG-350: render login types by their display name so Salesperson reads
    // as "Salesperson" rather than the raw role key.
    { field: 'roles', header: 'Roles', callback: (item: UserModel) => roleLabels(item.roles) },
    {
      field: 'created_at',
      header: 'Created At',
      sortable: true
    },
    {
      field: 'deleted_at',
      header: 'Status',
      type: 'badge',
      callback: (item: UserModel) => item.deleted_at ? 'Archived' : 'Active',
      getColor: (item: UserModel) => item.deleted_at ? 'error' : 'success'
    },
  ];

  rowActions = [
    {
      icon: this.EditIcon,
      routerLink: (item: UserModel) => ['/users', item.id, 'edit'],
      condition: (item: UserModel) => !item.deleted_at
    },
    {
      icon: this.ArchiveIcon,
      callback: (item: UserModel) => this.archiveUser(item),
      condition: (item: UserModel) => !item.deleted_at && this.isAdmin,
      classes: 'text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-500'
    },
    {
      icon: this.RestoreIcon,
      callback: (item: UserModel) => this.restoreUser(item),
      condition: (item: UserModel) => !!item.deleted_at && this.isAdmin,
      classes: 'text-green-500 hover:text-green-700 dark:text-green-400 dark:hover:text-green-500'
    }
  ];

  loadData() {
    let query = this.routeParams.toServiceQuery();

    const filterValue = this.archivedFilterControl.value;

    if (filterValue === 'all' || filterValue === 'archived') {
      query += (query ? '&' : '?') + 'include_archived=1';
    }

    this.service.getPaged(query).subscribe((res) => {
      // If showing archived only, filter out active users on the frontend
      if (filterValue === 'archived' && res.data) {
        res.data = res.data.filter(user => !!user.deleted_at);
      }
      this.usersPage = res;
    });
  }

  ngOnInit(): void {
    // Fetch current user and check if admin
    this.authService.getUser().subscribe({
      next: (user) => {
        this.currentUser = user;
        this.isAdmin = user?.roles?.includes('admin') ?? false;
        this.loadData();
      },
      error: () => {
        this.loadData();
      }
    });

    this.archivedFilterControl.valueChanges.subscribe(() => {
      this.loadData();
    });
  }

  handleRoute(params: RouteParams) {
    this.routeParams.sort = params.sort;
    this.routeParams.pageNum = params.pageNum;
    this.routeParams.pageSize = params.pageSize;
    this.loadData();
  }

  async archiveUser(user: UserModel): Promise<void> {
    if (!user.id) {
      return;
    }

    const confirmed = await this.confirmationModal.confirm({
      title: 'Archive user',
      message: `Are you sure you want to archive ${user.name}?`,
      confirmLabel: 'Archive User',
      cancelLabel: 'Keep User',
      confirmButtonStyle: 'danger',
    });

    if (!confirmed) {
      return;
    }

    this.service.archive(user.id).subscribe({
      next: (response) => {
        this.notificationService.success(response.message);
        this.loadData();
      },
      error: (error) => {
        this.notificationService.error(error.error?.message || 'Failed to archive user');
      }
    });
  }

  async restoreUser(user: UserModel): Promise<void> {
    if (!user.id) {
      return;
    }

    const confirmed = await this.confirmationModal.confirm({
      title: 'Restore user',
      message: `Are you sure you want to restore ${user.name}?`,
      confirmLabel: 'Restore User',
      cancelLabel: 'Cancel',
      confirmButtonStyle: 'success',
    });

    if (!confirmed) {
      return;
    }

    this.service.restore(user.id).subscribe({
      next: () => {
        this.notificationService.success('User restored successfully');
        this.loadData();
      },
      error: (error) => {
        this.notificationService.error(error.error?.message || 'Failed to restore user');
      }
    });
  }
}
